import { useState, useCallback } from 'react';
import styles from './create-license-plate-modal.module.sass';
import { useCreateLicensePlate } from '../use-license-plate-create';
import EditLicensePlate from 'modules/edit-license-plate';
import analytics from 'service/analytics';
import Button from 'componets/button';
import { licensePlateStrategy } from 'helpers/rule-validation';
import { useQueryClient } from '@tanstack/react-query';

export const CreateLicensePlateForm = ({ onClose, onLicensePlateCreateSucceeded }) => {
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [plateNumber, setPlateNumber] = useState('');
  const [plateNumberError, setPlateNumberError] = useState('');
  const [energySource, setEnergySource] = useState('fuel');
  const [vehicleType, setVehicleType] = useState('car');
  const [oilType, setOilType] = useState('gas_92');

  const { createLicensePlateMutateAsync } = useCreateLicensePlate();

  const handlePlateNumberChange = useCallback(number => {
    setPlateNumberError('');
    setPlateNumber(number);
  }, []);

  const handleEnergySourceChange = useCallback(source => {
    if (source === 'fuel') {
      analytics.track('車牌頁_新增車牌_油車_click');
    } else {
      analytics.track('車牌頁_新增車牌_電動車_click');
    }

    setEnergySource(source);
  }, []);

  const handleVehicleTypeChange = useCallback(
    type => {
      if (energySource === 'fuel') {
        switch (type) {
          case 'car':
            analytics.track('車牌頁_新增車牌_汽車_click');
            break;
          case 'moto':
            analytics.track('車牌頁_新增車牌_機車_click');
            break;
          case 'trailer':
            analytics.track('車牌頁_新增車牌_拖車_click');
            break;
          default:
            break;
        }
      } else {
        switch (type) {
          case 'car':
            analytics.track('車牌頁_新增車牌_電動汽車_click');
            break;
          case 'moto':
            analytics.track('車牌頁_新增車牌_電動機車_click');
            break;
          default:
            break;
        }
      }
      setVehicleType(type);
    },
    [energySource]
  );

  const handleOilTypeChange = useCallback(type => {
    switch (type) {
      case 'gas_92':
        analytics.track('車牌頁_新增車牌_92汽油_click');
        break;
      case 'gas_95':
        analytics.track('車牌頁_新增車牌_95汽油_click');
        break;
      case 'gas_98':
        analytics.track('車牌頁_新增車牌_98汽油_click');
        break;
      case 'diesel':
        analytics.track('車牌頁_新增車牌_柴油_click');
        break;
      default:
        break;
    }

    setOilType(type);
  }, []);

  const handlePlateNumberFocus = useCallback(() => analytics.track('車牌頁_新增車牌_輸入車牌_click'), []);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();

      analytics.licensePlatesCreateLicensePlateSaveClick();

      const validateResult = licensePlateStrategy(plateNumber);

      if (!validateResult.success) {
        setPlateNumberError(validateResult.error);
        return;
      }
      if (isLoading) return;

      setIsLoading(true);

      try {
        const { data } = await createLicensePlateMutateAsync({
          plateNumber,
          energySource,
          vehicleType,
          oilType,
        });

        await queryClient.invalidateQueries('license-plates');

        onLicensePlateCreateSucceeded(data?.data);
      } catch (error) {
        const { message } = error?.response.data.error ?? {};
        setPlateNumberError(message ?? '系統錯誤，請稍後再試');
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [
      plateNumber,
      energySource,
      vehicleType,
      oilType,
      isLoading,
      createLicensePlateMutateAsync,
      onLicensePlateCreateSucceeded,
      queryClient,
    ]
  );

  return (
    <form className={styles['create-license-plate-form']} onSubmit={handleSubmit}>
      <EditLicensePlate
        isEdit={false}
        title="新增付款車牌"
        plate={plateNumber}
        plateErrorMessage={plateNumberError}
        onPlateChange={handlePlateNumberChange}
        onPlateFocus={handlePlateNumberFocus}
        energySource={energySource}
        onEnergySourceSelect={handleEnergySourceChange}
        vehicleType={vehicleType}
        onVehicleTypeSelect={handleVehicleTypeChange}
        oilType={oilType}
        onOilTypeSelect={handleOilTypeChange}
      />
      <div className={styles['button-group']}>
        <Button
          type="button"
          variant="text"
          onClick={() => {
            analytics.licensePlatesCreateLicensePlateCancelClick();
            onClose();
          }}
        >
          取消
        </Button>
        <Button type="submit" loading={isLoading}>
          儲存，下一步
        </Button>
      </div>
    </form>
  );
};
